import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { List, Row, Col, Spin, Typography, Select, Space } from 'antd';
import { useQuery } from '@apollo/react-hooks';
import { queries } from '../../graphql/graphql';
import ProductListItem from './ProductListItem';
import _s from './index.less';

const { Text } = Typography;
const { Option } = Select;

const PAGE_SIZE = 20;
const PRODUCT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'objects', label: 'Objects' },
  { value: 'materials', label: 'Materials' },
  { value: 'materials-options', label: 'Materials from options' }
];

/**
 * ProductList component displays a list of products
 * @param {Object} props - Component props
 * @param {Array} props.products - Array of product objects
 */
const ProductList = ({ products }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState('all');

  const handleOpenProduct = useCallback((id, type) => {
    if (type === 'materials-options') {
      window.open(`https://3d.homewip.com/#!/admin/configurator/`, '_blank');
    } else {
      window.open(`https://3d.homewip.com/#!/product/${id}`, '_blank');
    }
  }, []);

  const filteredProducts = products.filter(
    product => selectedType === 'all' || product.type.toLowerCase() === selectedType
  );

  const handleTypeChange = value => {
    setSelectedType(value);
    setCurrentPage(1);
  };

  return (
    <div>
      <Row className={_s.filterRow}>
        <Col>
          <Space align="center">
            <Text>Filter by type:</Text>
            <Select value={selectedType} onChange={handleTypeChange} style={{ width: 200 }}>
              {PRODUCT_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>
      <List
        itemLayout="horizontal"
        dataSource={filteredProducts}
        className={_s.productList}
        pagination={{
          current: currentPage,
          onChange: setCurrentPage,
          pageSize: PAGE_SIZE,
          total: filteredProducts.length,
          position: 'bottom',
          align: 'center',
          showSizeChanger: false,
          showTotal: total => `Total ${total} items`
        }}
        renderItem={item => (
          <List.Item className={_s.listItem}>
            <ProductListItem product={item} onProductOpen={handleOpenProduct} />
          </List.Item>
        )}
      />
    </div>
  );
};

ProductList.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      type: PropTypes.string,
      avatar: PropTypes.shape({
        small: PropTypes.string,
        large: PropTypes.string
      }),
      data: PropTypes.shape({
        price: PropTypes.number,
        labor: PropTypes.number,
        url: PropTypes.string
      })
    })
  ).isRequired
};

/**
 * ProductsOfConfigurators component fetches and displays products
 */
export const ProductsOfConfigurators = () => {
  const { data = {}, loading, refetch } = useQuery(queries.GET_ALL_PRODUCTS_OF_CONFIGURATORS);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { productsOfConfigurators } = data;

  if (loading) {
    return (
      <div className={_s.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (!productsOfConfigurators || !productsOfConfigurators.list) {
    return (
      <div className={_s.notFoundContainer}>
        <Text type="secondary">Products not found</Text>
      </div>
    );
  }

  return (
    <div className={_s.productsContainer}>
      <Row>
        <Col span={24}>
          <ProductList products={productsOfConfigurators.list} />
        </Col>
      </Row>
    </div>
  );
};

export default ProductsOfConfigurators;
